import { TFunction } from '@wix/yoshi-flow-editor';
import {
  type BenefitDistribution,
  CustomScheduleOptionsDurationDurationUnit,
  DistributionStrategyType,
} from '@wix/ambassador-pricing-plans-plan-subscriptions-v1-plan-subscription/types';
import { formatDate } from '../../../../utils/displayUtils';

const CycleTranslations = {
  [CustomScheduleOptionsDurationDurationUnit.DAY]: 'app.details.benefits-schedule.cycle-day',
  [CustomScheduleOptionsDurationDurationUnit.WEEK]: 'app.details.benefits-schedule.cycle-week',
  [CustomScheduleOptionsDurationDurationUnit.MONTH]: 'app.details.benefits-schedule.cycle-month',
  [CustomScheduleOptionsDurationDurationUnit.YEAR]: 'app.details.benefits-schedule.cycle-year',
};

const NO_RENEWAL_DATE_PLACEHOLDER = '—';

interface GetBenefitsSchedulerParams {
  benefitDistribution: BenefitDistribution | undefined;
  regionalSettings: string;
  isPaused: boolean;
  t: TFunction;
}

export const getBenefitsScheduler = ({
  benefitDistribution,
  regionalSettings,
  isPaused,
  t,
}: GetBenefitsSchedulerParams): string[] => {
  const cycleValue = formatCycleValue(benefitDistribution, t);
  const lines = [`${t('app.details.benefits-schedule.cycle-label')} ${cycleValue}`];

  const formattedDate = formatNextRenewalDate(benefitDistribution, regionalSettings, isPaused);
  if (formattedDate) {
    lines.push(`${t('app.details.benefits-schedule.next-renewal-date-label')} ${formattedDate}`);
  }

  return lines;
};

const formatCycleValue = (benefitDistribution: BenefitDistribution | undefined, t: TFunction): string => {
  const settings = benefitDistribution?.settings;
  const cycleDuration = settings?.customScheduleOptions?.cycleDuration;
  const unit = cycleDuration?.unit;

  if (
    settings?.distributionStrategyType !== DistributionStrategyType.CUSTOM_SCHEDULE ||
    !unit ||
    unit === CustomScheduleOptionsDurationDurationUnit.UNKNOWN
  ) {
    return t('app.details.benefits-schedule.same-as-billing');
  }

  return t(CycleTranslations[unit], { count: cycleDuration?.count ?? 1 });
};

const formatNextRenewalDate = (
  benefitDistribution: BenefitDistribution | undefined,
  regionalSettings: string,
  isPaused: boolean,
): string | undefined => {
  const strategy = benefitDistribution?.settings?.distributionStrategyType;
  if (strategy !== DistributionStrategyType.CUSTOM_SCHEDULE || !benefitDistribution?.currentCycleEndDate) {
    return undefined;
  }
  if (isPaused) {
    return NO_RENEWAL_DATE_PLACEHOLDER;
  }
  return formatDate(benefitDistribution.currentCycleEndDate, regionalSettings) || undefined;
};
